/**
 * Prefetch utilities for React Query
 * These hooks help with prefetching data on hover to improve perceived performance
 */

import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from './queryKeys';
import { portfolioService } from '@/services/portfolioService';
import { fundService } from '@/services/fundService';
import { investorService } from '@/services/investorService';

/**
 * Hook for prefetching portfolio company details
 */
export const usePrefetchPortfolioCompany = () => {
  const queryClient = useQueryClient();

  return (companyId: string) => {
    // Only prefetch if not already in cache
    const cached = queryClient.getQueryData(
      queryKeys.portfolio.detail(companyId)
    );
    if (!cached) {
      queryClient.prefetchQuery({
        queryKey: queryKeys.portfolio.detail(companyId),
        queryFn: () => portfolioService.getPortfolioCompany(companyId),
        staleTime: 5 * 60 * 1000, // 5 minutes
      });
    }
  };
};

/**
 * Hook for prefetching fund details
 */
export const usePrefetchFund = () => {
  const queryClient = useQueryClient();

  return (fundId: string) => {
    // Only prefetch if not already in cache
    const cached = queryClient.getQueryData(queryKeys.funds.detail(fundId));
    if (!cached) {
      queryClient.prefetchQuery({
        queryKey: queryKeys.funds.detail(fundId),
        queryFn: () => fundService.getFund(fundId),
        staleTime: 5 * 60 * 1000, // 5 minutes
      });
    }
  };
};

/**
 * Hook for prefetching investor details
 */
export const usePrefetchInvestor = () => {
  const queryClient = useQueryClient();

  return (investorId: string) => {
    // Only prefetch if not already in cache
    const cached = queryClient.getQueryData(
      queryKeys.investors.detail(investorId)
    );
    if (!cached) {
      queryClient.prefetchQuery({
        queryKey: queryKeys.investors.detail(investorId),
        queryFn: () => investorService.getInvestor(investorId),
        staleTime: 5 * 60 * 1000, // 5 minutes
      });
    }
  };
};
